import { useContext, useState } from 'react';
import { toast } from 'react-hot-toast';

import { PartnerContext } from '../../contexts/PartnerContext';

import { Helmet } from "react-helmet";
import DataTable from 'react-data-table-component';
import UserProfilePhoto from '../UserProfilePhoto';
import SectionHeader from '../SectionHeader';
import TableLoader from '../TableLoader';
import PartnerStaffForm from './PartnerStaffForm';
import Modal from '../Modal';
import ConfirmPrompt from '../ConfirmPrompt';

import partnerCompanyService from '../../services/partnerCompanyService';
import PartnerAdminStaffView from './PartnerAdminStaffView';
import { formatDateToSydneyTimeZone } from '../../utils/dateUtils';

const PartnerAdminStaff = () => {
  const { partnerCompany, mutatePartnerCompany } = useContext(PartnerContext);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [selectedStaff, setSelectedStaff] = useState(null);

  const [isViewStaffModalOpen, setIsViewStaffModalOpen] = useState(false);
  const [isAddStaffModalOpen, setIsAddStaffModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isDeletingStaff, setIsDeletingStaff] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleStaffFormSubmit = async ({ fullName, email }) => {
    try {
      setIsLoading(true);

      if (selectedStaff) {
        // Update staff
      }
      else {
        await partnerCompanyService.inviteStaff({
          partnerCompanyId: partnerCompany._id,
          fullName,
          email
        });

        await mutatePartnerCompany();

        toast.success('User invited successfully');

        setIsLoading(false);
        setIsAddStaffModalOpen(false);
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to add staff');
    } finally {
      setIsLoading(false);
    }
  }

  const handleCloseStaffForm = () => {
    setIsAddStaffModalOpen(false);
    setSelectedStaff(null);
  }

  const handleDeleteStaff = (staff) => {
    setSelectedStaff(staff);
    setIsConfirmDeleteModalOpen(true);
  }

  const deleteStaff = async () => {
    try {
      setIsDeletingStaff(true);

      await partnerCompanyService.removeStaff({
        partnerCompanyId: partnerCompany._id,
        staffId: selectedStaff._id
      });

      await mutatePartnerCompany();

      toast.success('Staff deleted successfully');

      setSelectedStaff(null);
      setIsConfirmDeleteModalOpen(false);
    } catch (error) {
      console.log(error);
      toast.error('Failed to delete staff');
    } finally {
      setIsDeletingStaff(false);
    }
  }

  const handleStaffRowClicked = (staff) => {
    setSelectedStaff(staff);
    setIsViewStaffModalOpen(true);
  }

  const handleCloseDeleteStaffModal = () => {
    setSelectedStaff(null);
    setIsConfirmDeleteModalOpen(false);
  }

  const columns = [
    {
      cell: row => <UserProfilePhoto photoUrl={row.profilePicture} fullName={row.fullName} />,
      width: '75px'
    },
    {
      name: 'Name',
      selector: row => row.fullName,
      sortable: true,
      sortField: 'user.fullName'
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      sortField: 'user.email',
      grow: 2
    },
    {
      name: 'Date of Birth',
      selector: row => row.dateOfBirth ? formatDateToSydneyTimeZone(row.dateOfBirth) : 'N/A',
      sortable: true,
      sortField: 'user.dateOfBirth'
    },
    {
      name: 'Phone',
      selector: row => row.phoneNumber || 'N/A',
      sortable: true,
      sortField: 'user.phoneNumber'
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleDeleteStaff(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white  text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Staff | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Staff'
          actionsLeft={
            <button
              onClick={() => setIsAddStaffModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>}
        />

        <DataTable
          columns={columns}
          data={partnerCompany?.staff}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={handleStaffRowClicked}
          progressComponent={<TableLoader />}
          pagination
          paginationTotalRows={partnerCompany?.staff?.length}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
        />
      </div>

      <Modal isOpen={isAddStaffModalOpen}>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[600px] pb-1 overflow-visible">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{selectedStaff ? 'Update' : 'Add'} staff member</p>

            <p
              onClick={handleCloseStaffForm}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex items-center gap-6">
            <PartnerStaffForm
              staff={selectedStaff}
              isLoading={isLoading}
              onSubmit={handleStaffFormSubmit}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete ${selectedStaff?.fullName}?`}
          onConfirm={deleteStaff}
          onCancel={handleCloseDeleteStaffModal}
          isLoading={isDeletingStaff}
        />
      </Modal>

      <Modal isOpen={isViewStaffModalOpen}>
        <PartnerAdminStaffView
          user={selectedStaff}
          onCloseClicked={() => setIsViewStaffModalOpen(false)}
        />
      </Modal>
    </>
  )
}

export default PartnerAdminStaff;
